import en_GB from '@@/shared-components/locales/primevue/en_GB.json' assert { type: 'json' }
import cs_CZ from '@@/shared-components/locales/primevue/cs_CZ.json' assert { type: 'json' }
import ru_RU from '@@/shared-components/locales/primevue/ru_RU.json' assert { type: 'json' }

import type { PublicRuntimeConfig } from 'nuxt/schema'
import { locales } from '@@/shared-components/locales/index'
import type { PrimeVueConfiguration } from 'primevue/config'

export function useLocale() {
  const localeCookieKey = 'locale'
  const dataLangCookieKey = 'data-lang'
  const i18n = tryUseNuxtApp() ? useNuxtApp()?.$i18n : useI18n()
  const { public: config } = useRuntimeConfig()

  const previewLocale = useState<ValueOf<typeof locales> | null>('preview-locale', () => {
    if (import.meta.client) {
      return localStorage.getItem('preview-locale') as ValueOf<typeof locales> | null
    }
    return null
  })
  const locale = useState<ValueOf<typeof locales>>(
    'locale',
    () => locales[i18n.locale.value as keyof PublicRuntimeConfig['locales']]
  )
  const dataLang = useState<ValueOf<typeof locales>>(
    'dataLang',
    () => locales[i18n.locale.value as keyof PublicRuntimeConfig['locales']]
  )
  const setLocale = async (
    newLocale: ValueOf<typeof locales>,
    i18nLocale: keyof PublicRuntimeConfig['locales']
  ) => {
    locale.value = newLocale
    const nuxtApp = useNuxtApp()
    setPrimeVueLocale(nuxtApp.vueApp.config.globalProperties.$primevue.config)
    useCookie(localeCookieKey, { sameSite: true, secure: true }).value = newLocale
    await loadMessages(newLocale, i18nLocale)
    await i18n.setLocale(i18nLocale)
  }

  const setPrimeVueLocale = (config: PrimeVueConfiguration) => {
    const locales = {
      en_GB,
      cs_CZ,
      ru_RU,
    }
    config.locale = { ...locales[locale.value] }
  }

  const setDataLang = async (newDatalang: ValueOf<typeof locales>) => {
    dataLang.value = newDatalang
    useCookie(dataLangCookieKey, { sameSite: true, secure: true }).value = newDatalang
  }

  const setPreviewLocale = (locale: ValueOf<typeof locales>) => {
    if (previewLocale.value === locale) {
      previewLocale.value = null
      localStorage.removeItem('preview-locale')
    } else {
      previewLocale.value = locale
      localStorage.setItem('preview-locale', locale)
    }
  }
  const instange = getCurrentInstance()
  let queryClient: ReturnType<typeof useQueryClient> | null = instange ? useQueryClient() : null

  const loadMessages = async (
    lang: ValueOf<typeof locales>,
    i18nLocale: keyof PublicRuntimeConfig['locales']
  ) => {
    try {
      if (!queryClient) {
        queryClient = useQueryClient()
      }
      const messages = await queryClient.fetchQuery(
        messagesQuery(
          { locale: locale.value, dataLang: dataLang.value },
          {
            domain:
              config.translationsDomain ||
              (nuxtApp.$sharedComponentsOptions &&
                'options' in nuxtApp.$sharedComponentsOptions &&
                nuxtApp.$sharedComponentsOptions?.options &&
                'i18n' in nuxtApp.$sharedComponentsOptions?.options &&
                typeof nuxtApp.$sharedComponentsOptions?.options?.i18n === 'object' &&
                'translationsDomain' in nuxtApp.$sharedComponentsOptions?.options?.i18n &&
                nuxtApp.$sharedComponentsOptions?.options?.i18n?.translationsDomain),
            lang,
          }
        )
      )

      if (messages && typeof messages === 'object') {
        i18n.setLocaleMessage(i18nLocale, messages)
      }
      return messages
    } catch (e) {
      console.error(e)
    }
  }

  return {
    locale,
    dataLang,
    previewLocale,
    setLocale,
    setDataLang,
    setPreviewLocale,
    setPrimeVueLocale,
    loadMessages,
  }
}
